// useKeyboardSafeSheet — keep the focused input of a bottom-sheet modal
// visible when the on-screen keyboard opens.
//
// 2026-09-16 — coach report (mobile app): "when I tap the message box in
// Invite Spares / Message Players the box disappears — hidden behind the
// keyboard or something." Mechanism, reconstructed from the layouts:
//
//   1. The sheet is `position: fixed; top: 0; height: var(--mobile-h)`
//      with `items-end`, and the sheet body is `overflow-y: auto` capped
//      at 85% of --mobile-h (see GamePage / SchedulePage `Modal`).
//   2. Tapping the textarea focuses it → the keyboard animates in →
//      --mobile-h shrinks (MobileLayout's visualViewport observer on the
//      web, the Keyboard plugin listener in Capacitor).
//   3. The sheet gets shorter, but its scroll container keeps the SAME
//      scrollTop. The textarea sits at the bottom of a long list (spares
//      list / recipient list above it), so it is now clipped below the
//      sheet's new bottom edge — exactly "hidden behind the keyboard".
//      The browser's own focus-scroll happened at step 2, BEFORE the
//      container was resized, so it doesn't help.
//   4. On iOS Safari the visual viewport can additionally scroll
//      (visualViewport.offsetTop > 0) to reveal the field; a fixed
//      wrapper anchored at top:0 then partly leaves the visible area.
//
// This hook fixes 3 and 4 generically for any sheet:
//   • On focusin inside the sheet, and again whenever the visual
//     viewport / --keyboard-h changes, re-scroll the focused editable
//     into view INSIDE the sheet (block: 'center'), after the keyboard
//     animation has had time to settle (two retries).
//   • Exposes `vvTop` — visualViewport.offsetTop while an editable is
//     focused — so the wrapper can pin itself to the *visible* top
//     (`top: vvTop`) instead of the layout-viewport top.
//
// Usage:
//   const { sheetRef, vvTop } = useKeyboardSafeSheet();
//   <div style={{ position:'fixed', top: vvTop, height:'var(--mobile-h)' }}>
//     <div ref={sheetRef} style={{ overflowY:'auto' }}>…</div>
//   </div>
import { useEffect, useRef, useState } from 'react';

const isEditable = (el) =>
  !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true);

export function useKeyboardSafeSheet() {
  const sheetRef = useRef(null);
  const [vvTop, setVvTop] = useState(0);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    let timers = [];
    const clearTimers = () => { timers.forEach(clearTimeout); timers = []; };

    // Only devices with an on-screen keyboard need this: touch devices
    // (`pointer: coarse` — phones, tablets, the Capacitor WebView), or
    // any time the native keyboard class is on <html>. A desktop mouse
    // user clicking a field in a scrollable modal would otherwise get a
    // small, pointless smooth-scroll jump on every focus.
    const onScreenKeyboardLikely = () => {
      try {
        if (document.documentElement.classList.contains('keyboard-open')) return true;
        return window.matchMedia('(pointer: coarse)').matches;
      } catch { return false; }
    };

    const revealFocused = () => {
      const sheet = sheetRef.current;
      const el = document.activeElement;
      if (!sheet || !isEditable(el) || !sheet.contains(el)) return;
      if (!onScreenKeyboardLikely()) return;
      // Scroll within the sheet only — `block: 'center'` keeps the field
      // clear of both the sticky header and the keyboard edge. `nearest`
      // for the inline axis avoids any sideways jump.
      try { el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' }); }
      catch { try { el.scrollIntoView(); } catch { /* ignore */ } }
    };

    // Keyboard show/hide is animated (~250–350 ms on iOS, similar on
    // Android) and the viewport resize lands somewhere in the middle.
    // Re-run a few times so at least one pass happens AFTER the final
    // size is known; extra passes on an already-visible field are no-ops
    // visually (smooth scroll to the same spot).
    const scheduleReveal = () => {
      clearTimers();
      [60, 320, 650].forEach(ms => timers.push(setTimeout(revealFocused, ms)));
    };

    const updateTop = () => {
      const el = document.activeElement;
      const focused = isEditable(el) && sheetRef.current?.contains(el);
      const top = focused && vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
      setVvTop(prev => (prev === top ? prev : top));
    };

    const onFocusIn = (e) => {
      if (!sheetRef.current?.contains(e.target)) return;
      scheduleReveal();
      updateTop();
    };
    const onFocusOut = () => { clearTimers(); setVvTop(0); };
    const onViewport = () => { updateTop(); scheduleReveal(); };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    if (vv) {
      vv.addEventListener('resize', onViewport, { passive: true });
      vv.addEventListener('scroll', onViewport, { passive: true });
    }
    window.addEventListener('resize', onViewport, { passive: true });
    // Capacitor: the native keyboard toggles the `keyboard-open` class on
    // <html> (useCapacitorPlugins) — no visualViewport resize is
    // guaranteed there, so watch the class instead.
    const mo = new MutationObserver(onViewport);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });

    return () => {
      clearTimers();
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      if (vv) {
        vv.removeEventListener('resize', onViewport);
        vv.removeEventListener('scroll', onViewport);
      }
      window.removeEventListener('resize', onViewport);
      mo.disconnect();
    };
  }, []);

  return { sheetRef, vvTop };
}

export default useKeyboardSafeSheet;
